/**
 * The colleagues this user may message, for the new-chat picker.
 *
 * `GET /api/v3/chat/users` with no filter — department-scoped server-side, so
 * the list is already the right people rather than everyone at the company.
 *
 * The picker filters client-side, so it needs the whole directory: we page
 * through with `useInfiniteQuery` and pull every page as it arrives rather
 * than stopping at the first 200.
 */
import { v3 } from "backoffice-api"
import { useEffect, useMemo } from "react"
import type { Colleague } from "../components/NewChat"
import { reportChatError } from "../reportChatError"

/** Rows per request; the picker filters the pages once they're all in. */
const PAGE_SIZE = 200

export function useChatDirectory(): Colleague[] | undefined {
  const { data, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
    v3.getChatUsers.useInfiniteQuery([{ "page[size]": PAGE_SIZE }], {
      initialPageParam: [{ "page[number]": 1 }] as const,
      getNextPageParam: (lastPage) => {
        const { current_page, total_pages } = lastPage.meta.pagination
        if (current_page >= total_pages) return undefined
        return [{ "page[number]": current_page + 1 }] as const
      },
    })

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // The picker falls back to an empty list on error; report so the empty state
  // is not the only trace of a directory that failed to load.
  useEffect(() => {
    if (error) reportChatError("failed to load the chat directory", error)
  }, [error])

  return useMemo(
    () =>
      data?.pages.flatMap((page) =>
        page.data.map((user) => ({
          id: user.id,
          displayName: user.display_name ?? user.id,
          avatar: user.avatar || null,
        }))
      ),
    [data]
  )
}
